(() => {
  'use strict';

  const SUPPORTED_MARKETS = ['US', 'GB', 'DE', 'FR', 'IT', 'ES'];
  const activeMarket = () => String(document.body?.dataset.msxShopCountry || 'US').toUpperCase();

  const setAmazonButtonState = (button, market) => {
    const safeMarket = SUPPORTED_MARKETS.includes(market) ? market : 'US';
    const url = button.getAttribute(`data-url-${safeMarket.toLowerCase()}`) || button.getAttribute('data-url-us') || '';
    const label = button.dataset.label || button.textContent.trim() || 'Buy on Amazon';
    const icon = button.querySelector('[data-msx-button-icon]');
    const text = button.querySelector('[data-msx-amazon-label]');
    const validUrl = /^https?:\/\//i.test(url);

    if (validUrl) {
      button.href = url;
      button.target = button.dataset.newtab === 'false' ? '_self' : '_blank';
      button.rel = button.target === '_blank' ? 'noopener nofollow sponsored' : '';
      button.classList.remove('is-disabled');
      button.removeAttribute('aria-disabled');
      button.hidden = false;
    } else {
      button.removeAttribute('href');
      button.removeAttribute('target');
      button.removeAttribute('rel');
      button.classList.add('is-disabled');
      button.setAttribute('aria-disabled', 'true');
      button.hidden = button.dataset.hideWhenMissing !== 'false';
    }
    if (text) text.textContent = validUrl ? label : button.dataset.missingLabel || label;
    if (icon) icon.hidden = button.dataset.showIcon === 'false';
    button.setAttribute('aria-label', validUrl ? label : button.dataset.missingLabel || label);
  };

  const initializeSelectors = (scope = document) => {
    const market = activeMarket();
    document.documentElement.dataset.msxMarket = market;
    scope.querySelectorAll('[data-msx-market-selector]').forEach((select) => {
      if ([...select.options].some((option) => option.value === market)) select.value = market;
      if (select.dataset.msxMarketReady === 'true') return;
      select.dataset.msxMarketReady = 'true';
      select.addEventListener('change', () => {
        const form = select.closest('form');
        if (!form) return;
        // A disabled select is not submitted: keep country_code enabled.
        let returnTo = form.querySelector('[name="return_to"]');
        if (!returnTo) {
          returnTo = document.createElement('input');
          returnTo.type = 'hidden';
          returnTo.name = 'return_to';
          form.append(returnTo);
        }
        returnTo.value = window.location.pathname + window.location.search;
        form.submit();
      });
    });
    scope.querySelectorAll('[data-msx-language-selector]').forEach((select) => {
      if (select.dataset.msxLanguageReady === 'true') return;
      select.dataset.msxLanguageReady = 'true';
      select.addEventListener('change', () => {
        const form = select.closest('form');
        if (!form) return;
        let returnTo = form.querySelector('[name="return_to"]');
        if (!returnTo) {
          returnTo = document.createElement('input');
          returnTo.type = 'hidden';
          returnTo.name = 'return_to';
          form.append(returnTo);
        }
        returnTo.value = window.location.pathname + window.location.search;
        form.submit();
      });
    });
    scope.querySelectorAll('[data-msx-amazon-button]').forEach((button) => setAmazonButtonState(button, market));
  };

  const initSliders = (scope = document) => {
    scope.querySelectorAll('[data-msx-slider]').forEach((slider) => {
      if (slider.dataset.msxSliderReady === 'true') return;
      slider.dataset.msxSliderReady = 'true';
      const track = slider.querySelector('[data-msx-slider-track]');
      const slides = [...slider.querySelectorAll('[data-msx-slide]')];
      const dots = [...slider.querySelectorAll('[data-msx-dot]')];
      if (!track || slides.length < 2) return;
      let index = 0;
      const go = (next) => {
        index = (next + slides.length) % slides.length;
        track.style.transform = `translateX(-${index * 100}%)`;
        slides.forEach((slide, i) => {
          slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
          slide.inert = i !== index;
        });
        dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === index);
          dot.setAttribute('aria-current', i === index ? 'true' : 'false');
        });
      };
      slider.querySelector('[data-msx-prev]')?.addEventListener('click', () => go(index - 1));
      slider.querySelector('[data-msx-next]')?.addEventListener('click', () => go(index + 1));
      dots.forEach((dot, i) => dot.addEventListener('click', () => go(i)));
      let startX = null;
      track.addEventListener('touchstart', (event) => { startX = event.touches[0].clientX; }, { passive: true });
      track.addEventListener('touchend', (event) => {
        if (startX === null) return;
        const delta = event.changedTouches[0].clientX - startX;
        if (Math.abs(delta) > 40) go(index + (delta < 0 ? 1 : -1));
        startX = null;
      }, { passive: true });
      const interval = Number(slider.dataset.autoplayInterval || 0);
      if (interval > 0 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        let timer = window.setInterval(() => go(index + 1), interval);
        slider.addEventListener('mouseenter', () => window.clearInterval(timer));
        slider.addEventListener('mouseleave', () => { timer = window.setInterval(() => go(index + 1), interval); });
      }
      go(0);
    });
  };

  const init = (scope = document) => {
    // Theme-editor URL settings may be root-relative. Retain the locale/market
    // on internal navigation without touching Amazon, admin or checkout URLs.
    const root = (window.Shopify?.routes?.root || window.routes?.root_url || '/').replace(/\/?$/, '/');
    if (root !== '/') scope.querySelectorAll('a[href]').forEach(link => {
      const href = link.getAttribute('href');
      if (href === '/' || /^\/(products|collections|pages|blogs|search|policies)([/?#]|$)/.test(href)) {
        link.setAttribute('href', root + href.slice(1));
      }
    });
    initializeSelectors(scope);
    initSliders(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:reorder', () => init());
})();
